import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import cn from 'classnames';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { debounce, isEqual } from 'lodash';
import { Popover, Transition } from '@headlessui/react';
import {
  CalendarBlank,
  CaretDoubleLeft,
  CaretDoubleRight,
} from '@phosphor-icons/react';

import Button from 'partial/components/Button';

function BadgeButton({ name, onClick, active, children }) {
  const handleSelect = (e) => {
    e.preventDefault();
    onClick(name);
  };
  return (
    <a
      className={cn(
        'flex-1 text-center px-2.5 py-1 rounded text-sm font-medium transition-colors',
        active
          ? 'bg-brand-600 text-white hover:bg-brand-700'
          : 'text-neutral-700 hover:bg-neutral-200',
      )}
      href="/"
      onClick={handleSelect}
    >
      {children}
    </a>
  );
}

BadgeButton.propTypes = {
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  children: PropTypes.string.isRequired,
};

const FORMAT_BY_MODE = {
  day: 'MM/DD/YYYY',
  month: 'MMM YYYY',
  year: 'YYYY',
};

const MAP_TYPE = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

function Callback({ trigger, onChange }) {
  React.useEffect(() => {
    onChange(trigger);
    // eslint-disable-next-line
  }, [trigger]);
  return null;
}

function HistogramRange({
  onChange,
  defaultValue,
  defaultMode,
  switchMode,
  panelClassName,
}) {
  const [mode, setMode] = React.useState(defaultMode);
  const [date, setDate] = React.useState(new Date(defaultValue));

  const popOverRef = React.useRef();

  const handleSet = (newValue) => {
    // popOverRef.current.click();
    setMode(newValue);
  };
  const isOpenedOnce = React.useRef(false);
  // eslint-disable-next-line
  const fetch = React.useCallback(
    debounce((d, m) => {
      const payload = {};
      payload.type = MAP_TYPE[m];
      payload.date_from = dayjs(d).startOf(m).format('YYYY-MM-DD 00:00:00');
      payload.date_to = dayjs(d).endOf(m).format('YYYY-MM-DD 23:59:59');
      onChange((state) => {
        const newState = {
          ...state,
          ...payload,
        };
        return isEqual(state, newState) ? state : newState;
      });
    }, 300),
    [],
  );
  const handleCallback = (e) => {
    if (e || !isOpenedOnce.current) {
      isOpenedOnce.current = true;
      return;
    }
    fetch(date, mode);
  };
  const handlePrev = () => {
    const newDate = dayjs(date).subtract(1, mode).toDate();
    fetch(newDate, mode);
    setDate(newDate);
  };
  const handleNext = () => {
    const newDate = dayjs(date).add(1, mode).toDate();
    fetch(newDate, mode);
    setDate(newDate);
  };
  const label = React.useMemo(
    () => dayjs(date).format(FORMAT_BY_MODE[mode]),
    [mode, date],
  );
  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Callback trigger={open} onChange={handleCallback} />
          <div className="flex space-x-1">
            <Button
              icon={CaretDoubleLeft}
              outline
              size="sm"
              className="!rounded-r-none"
              onClick={handlePrev}
            />
            <Popover.Button
              as={Button}
              ref={popOverRef}
              outline
              size="sm"
              className="!rounded-none -mx-px"
            >
              <div className="flex items-center gap-2">
                <CalendarBlank size={14} />
                <span className="tabular-nums">{label}</span>
              </div>
            </Popover.Button>
            <Button
              icon={CaretDoubleRight}
              outline
              size="sm"
              className="!rounded-l-none"
              onClick={handleNext}
            />
          </div>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            as={Fragment}
          >
            <Popover.Panel className={cn('absolute z-50 w-80', panelClassName)}>
              <div className="p-1 pt-3 bg-white rounded-lg border border-neutral-200 shadow-popover mt-2">
                {switchMode && (
                  <div className="gap-2 mb-3 bg-neutral-100 w-auto flex justify-center rounded-md p-1">
                    <BadgeButton
                      name="day"
                      onClick={handleSet}
                      active={mode === 'day'}
                    >
                      Day
                    </BadgeButton>
                    <BadgeButton
                      name="month"
                      onClick={handleSet}
                      active={mode === 'month'}
                    >
                      Month
                    </BadgeButton>
                    <BadgeButton
                      name="year"
                      onClick={handleSet}
                      active={mode === 'year'}
                    >
                      Year
                    </BadgeButton>
                  </div>
                )}
                <div>
                  <DatePicker
                    selected={date}
                    onChange={setDate}
                    inline
                    showMonthYearPicker={mode === 'month'}
                    showYearPicker={mode === 'year'}
                    disabledKeyboardNavigation
                  />
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

HistogramRange.defaultProps = {
  defaultMode: 'year',
  panelClassName: '',
  switchMode: true,
};

HistogramRange.propTypes = {
  switchMode: PropTypes.bool,
  defaultMode: PropTypes.string,
  panelClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string.isRequired,
};

Callback.propTypes = {
  onChange: PropTypes.func.isRequired,
  trigger: PropTypes.bool.isRequired,
};

export default HistogramRange;
